import { useMemo, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Box, Card, CardActionArea, Divider, Grid, Skeleton, Typography } from "@mui/material"
import { faMagnifyingGlass, faStore } from "@fortawesome/pro-regular-svg-icons"

import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { tailwindPalette } from "../../../../../../modules/theme/tailwindPalette"
import { EmptyContent } from "../../../../skeleton/EmptyContent"
import { StandardDialog } from "../../../../dialogs/StandardDialog"
import { FilterSearch } from "../../FilterSearch"

import type { TakeawayKitchen } from "../takeawayTypes"
import { useApiImageUrl } from "../useApiImageUrl"

const SKELETON_COUNT = 3
const SEARCH_MIN_KITCHENS = 10
const DEFAULT_GRID_BREAKPOINTS = { xs: 12, sm: 6, md: 4 } as const

function matchesSearch(kitchen: TakeawayKitchen, query: string) {
    const fields = [kitchen.name, kitchen.brandingDetails?.description]
    return fields.some((f) => f?.toLowerCase().includes(query))
}

function getGridBreakpoints(count: number) {
    if (count <= 1) return { xs: 12 } as const
    if (count === 2) return { xs: 12, sm: 6 } as const
    return DEFAULT_GRID_BREAKPOINTS
}

const useStyles = makeStyles()((theme) => ({
    content: {
        padding: "0 3rem 3rem",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        minHeight: "16.5rem",
        justifyContent: "center",
    },
    toolbar: {
        display: "flex",
        justifyContent: "flex-end",
    },
    kitchenCard: {
        borderRadius: "1rem",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        boxShadow: "0px 2px 6px 0px rgba(0, 0, 0, 0.08)",
    },
    kitchenMedia: {
        width: "100%",
        height: "8rem",
    },
    kitchenImage: {
        objectFit: "cover",
        display: "block",
    },
    kitchenImagePlaceholder: {
        backgroundColor: theme.palette.tailwind.slate[200],
    },
    kitchenContent: {
        display: "flex",
        flexDirection: "column",
    },
    kitchenName: {
        padding: ".375rem .75rem",
        fontWeight: 700,
        color: theme.palette.tailwind.slate[700],
    },
    kitchenDescription: {
        padding: ".25rem .75rem",
        color: theme.palette.tailwind.slate[500],
    },
    kitchenDivider: {
        borderStyle: "dashed",
        borderColor: theme.palette.tailwind.slate[200],
    },
    skeletonImage: {
        transform: "none",
        borderRadius: 0,
    },
}))

type KitchenCardProps = Readonly<{
    kitchen: TakeawayKitchen
    onSelect: (kitchen: TakeawayKitchen) => void
}>

function KitchenCard({ kitchen, onSelect }: KitchenCardProps) {
    const { classes, cx } = useStyles()
    const { description, imageUrl } = kitchen.brandingDetails ?? {}
    const resolvedImageUrl = useApiImageUrl(imageUrl)

    return (
        <Card className={classes.kitchenCard} elevation={0}>
            <CardActionArea onClick={() => onSelect(kitchen)}>
                {resolvedImageUrl ? (
                    <img
                        src={resolvedImageUrl}
                        className={cx(classes.kitchenMedia, classes.kitchenImage)}
                        alt={kitchen.name}
                    />
                ) : (
                    <Box className={cx(classes.kitchenMedia, classes.kitchenImagePlaceholder)} />
                )}
                <Box className={classes.kitchenContent}>
                    <Typography variant="subtitle1" className={classes.kitchenName}>
                        {kitchen.name}
                    </Typography>
                    {description && (
                        <>
                            <Divider className={classes.kitchenDivider} />
                            <Typography variant="body2" className={classes.kitchenDescription}>
                                {description}
                            </Typography>
                        </>
                    )}
                </Box>
            </CardActionArea>
        </Card>
    )
}

function KitchenCardSkeleton() {
    const { classes, cx } = useStyles()

    return (
        <Card className={classes.kitchenCard} elevation={0}>
            <Skeleton
                variant="rectangular"
                animation="wave"
                className={cx(classes.kitchenMedia, classes.skeletonImage)}
            />
            <Box className={classes.kitchenContent}>
                <Typography variant="subtitle1" className={classes.kitchenName}>
                    <Skeleton variant="text" animation="wave" width="70%" />
                </Typography>
                <Divider className={classes.kitchenDivider} />
                <Typography variant="body2" className={classes.kitchenDescription}>
                    <Skeleton variant="text" animation="wave" width="50%" />
                </Typography>
            </Box>
        </Card>
    )
}

function KitchenSelectorSkeleton() {
    return (
        <Grid container spacing={3}>
            {Array.from({ length: SKELETON_COUNT }, (_, i) => (
                <Grid item {...DEFAULT_GRID_BREAKPOINTS} key={i}>
                    <KitchenCardSkeleton />
                </Grid>
            ))}
        </Grid>
    )
}

function KitchenSelectorEmpty() {
    return (
        <Box>
            <LocalizedStrict id="takeaway-no-shops-empty" attrs={{ title: true }}>
                <EmptyContent
                    icon={faStore}
                    backgroundColor={tailwindPalette.slate[400]}
                    title="No takeaway shops available"
                    description=""
                />
            </LocalizedStrict>
        </Box>
    )
}

function KitchenSelectorNoResults() {
    return (
        <Box>
            <LocalizedStrict id="takeaway-menu-no-search-results-empty" attrs={{ title: true }}>
                <EmptyContent
                    icon={faMagnifyingGlass}
                    backgroundColor={tailwindPalette.slate[400]}
                    title="No results"
                    description=""
                />
            </LocalizedStrict>
        </Box>
    )
}

type TakeawayKitchenSelectorDialogProps = Readonly<{
    isLoading: boolean
    kitchens: TakeawayKitchen[]
    onSelect: (kitchen: TakeawayKitchen) => void
    onClose: () => void
}>

export function TakeawayKitchenSelectorDialog({
    isLoading,
    kitchens,
    onSelect,
    onClose,
}: TakeawayKitchenSelectorDialogProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const [search, setSearch] = useState("")

    const filteredKitchens = useMemo(() => {
        const query = search.trim().toLowerCase()
        if (!query) return kitchens
        return kitchens.filter((k) => matchesSearch(k, query))
    }, [kitchens, search])

    const showSearch = !isLoading && kitchens.length >= SEARCH_MIN_KITCHENS

    function renderBody() {
        if (isLoading) return <KitchenSelectorSkeleton />
        if (!kitchens.length) return <KitchenSelectorEmpty />
        if (!filteredKitchens.length) return <KitchenSelectorNoResults />
        const gridBreakpoints = getGridBreakpoints(filteredKitchens.length)
        return (
            <Grid container spacing={3}>
                {filteredKitchens.map((kitchen, index) => (
                    <Grid item {...gridBreakpoints} key={`${kitchen.id}-${index}`}>
                        <KitchenCard kitchen={kitchen} onSelect={onSelect} />
                    </Grid>
                ))}
            </Grid>
        )
    }

    return (
        <StandardDialog
            open
            onClose={onClose}
            dialogTitle={l10n.getString("takeaway-kitchen-selector-title")}
            maxWidthProp="md"
        >
            <Box className={classes.content}>
                {showSearch && (
                    <Box className={classes.toolbar}>
                        <FilterSearch search={search} setSearch={setSearch} defaultActive activeWidth="18rem" />
                    </Box>
                )}
                {renderBody()}
            </Box>
        </StandardDialog>
    )
}
